import { MMProductCard } from "./components/mix-and-match-product-card";
import { MMCartSummary } from "./components/mix-and-match-cart-summary";
import { MMBoxProduct } from "./components/mm-box-product";
import "./components/g-sidebar";
import "./components/mm-product-popup";

// Type definitions
interface DiscountTier {
  variantId: string;
  quantity: number;
  discount: number;
}

interface Variant {
  id: string | number;
  category: string;
  quantity: number;
  title: string;
  image: string;
  price: number;
  type: string;
  handle: string;
  inventory_quantity: number;
  product_type: string;
  propertiesList?: Record<string, string>[];
}

interface BoxProduct {
  id: string | number;
  quantity: number;
  title: string;
  image: string;
  price: number;
  handle: string;
  type: string;
}

interface Gift {
  id: string | number;
  goal_quantity: number;
  gift_quantity: number;
  type: string;
  handle: string;
}

interface MixAndMatchState {
  totalPrice: number;
  totalPriceWithoutDiscount: number;
  totalQuantity: number;
  minimumQuantity: number;
  gifts: { [key: string]: Gift };
  currentDiscount: number | null;
  variants: { [key: string]: Variant };
  boxProducts: { [key: string]: BoxProduct };
  discountTiers: DiscountTier[];
}

interface StateUpdate {
  action: string;
  previousState: MixAndMatchState | null;
  state: MixAndMatchState;
}

type StateCallback = (stateUpdate: StateUpdate) => void;

interface VariantData {
  id: string | number;
  category: string;
  quantity: number;
  title?: string;
  image?: string;
  price?: number;
  type?: string;
  handle?: string;
  inventory_quantity?: number;
  product_type?: string;
  properties?: Record<string, string>;
  replaceQuantity?: boolean;
}


interface BoxProductData {
  id: string | number;
  quantity: number;
  title: string;
  image: string;
  price: number;
  handle: string;
}

interface GiftState {
  gift_qnt: number;
  gift_handle: string;
}

interface CartItem {
  key: string;
  properties: {
    _bundle_addons?: string;
    '__foxsell:dynamic_add_on_bundle_items'?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface Cart {
  items: CartItem[];
}

declare global {
  interface Window {
    formatMoney: (cents: string | number, format?: string) => string;
    moneyFormat: string;
    mixMatchStore: MixAndMatchStore;
    mmConfig: any;
    cachedCart: Cart;
    fetchAndCacheCart: () => Promise<Cart>;
    cart: Cart;
    heightManager: DynamicHeightManager;
    Shopify: {
      routes: {
        root: string;
      };
    };
  }
}

window.formatMoney = function (cents: string | number, format?: string): string {
  if (typeof cents == 'string') { cents = cents.replace('.', ''); }
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = (format || window.moneyFormat);

  function defaultOption(opt: any, def: any): any {
    return (typeof opt == 'undefined' ? def : opt);
  }

  function formatWithDelimiters(number: number, precision?: number, thousands?: string, decimal?: string): string {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');

    if (isNaN(number) || number == null) { return '0'; }

    const numberStr = (number / 100.0).toFixed(precision);

    const parts = numberStr.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? (decimal + parts[1]) : '';

    return dollars + cents;
  }

  const match = formatString?.match(placeholderRegex);
  if (!match) return formatString || "";

  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents as number, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents as number, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents as number, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents as number, 0, '.', ',');
      break;
  }

  return formatString?.replace(placeholderRegex, value) || "";
};

class MixAndMatchStore {
  state: MixAndMatchState;
  previousState: MixAndMatchState | null;
  discountTiers: DiscountTier[];
  subscribers: Set<StateCallback>;

  constructor() {
    this.state = {
      totalPrice: 0,
      totalPriceWithoutDiscount: 0,
      totalQuantity: 0,
      minimumQuantity: 0,
      gifts: {},
      currentDiscount: null,
      variants: {},
      boxProducts: {},
      discountTiers: [],
    };

    this.previousState = null;
    this.discountTiers = [];
    this.subscribers = new Set();

    this.init();
  }

  init(): void {
    this.notify("INIT_STARTED");
    try {
      const mmConfig = sessionStorage.getItem("mm-config");
      if (!mmConfig) {
        throw new Error("Missing mix&match configuration");
      }

      const rawConfig = JSON.parse(mmConfig).options;
      window.mmConfig = JSON.parse(mmConfig);
      if (!Array.isArray(rawConfig)) {
        throw new Error("Invalid mix&match configuration format");
      }

      // Transform into simplified structure
      this.state.discountTiers = this._parseDiscountConfig(rawConfig);
      if (this.state.discountTiers.length > 0) {
        this.state.minimumQuantity = this.state.discountTiers[0].quantity;
      }
    }
    catch (err) {
      console.error("Initialization failed:", err);
    }

    try {
      const stateRaw = sessionStorage.getItem("mm-state");
      const giftsRaw = sessionStorage.getItem("mm-addon-settings");

      const parsedState = stateRaw ? JSON.parse(stateRaw) : null;
      const parsedGifts = giftsRaw ? JSON.parse(giftsRaw) : null;

      if (parsedState) {
        this.state = parsedState;

        if (parsedGifts && (!parsedState.gifts || Object.keys(parsedState.gifts).length === 0)) {
          this.state.gifts = parsedGifts;
        }
      } else if (parsedGifts) {
        this.state.gifts = parsedGifts;
      }
    } catch (err) {
      console.error("Initialization failed:", err);
    }

    this.notify("INIT_DONE");
    // Note: edit_bundle mode is now handled after custom elements are defined
    // See the DOMContentLoaded handler at the bottom of this file
  }

  clearState(): void {
    for (const productKey in this.state.variants) {
      const product = this.state.variants[productKey];
      product.quantity = 0;
      product.propertiesList = []; // Clear engraving properties
    }
    for (const boxKey in this.state.boxProducts) {
      const boxProduct = this.state.boxProducts[boxKey];
      boxProduct.quantity = 0;
    }
    this.state.totalPrice = 0;
    this.state.totalPriceWithoutDiscount = 0;
    this.state.totalQuantity = 0;
    this.state.currentDiscount = 0;

    for (const giftKey in this.state.gifts) {
      const gift = this.state.gifts[giftKey];
      gift.gift_quantity = 1;
    }
  }

  persist(): void {
    sessionStorage.setItem("mm-state", JSON.stringify(this.state));
  }

  updateGiftCheckedState(state: GiftState): void {
    const { gift_qnt: giftQuantity, gift_handle: giftHandle } = state;
    const gift = this.state.gifts[giftHandle];
    if (!gift || isNaN(Number(giftQuantity)) || !giftHandle) {
      console.warn("Invalid gift data");
      return;
    }
    gift.gift_quantity = Number(giftQuantity);
    this.persist();
    this.notify("GIFT_CHECKED_STATE_UPDATED");
  }

  _parseDiscountConfig(rawConfig: any[]): DiscountTier[] {
    return rawConfig
      .map((tier) => {
        const [variantId, config] = Object.entries(tier)[0];
        return {
          variantId: variantId as string,
          quantity: (config as any).quantity,
          discount: (config as any).price.value,
        };
      })
      .sort((a, b) => a.quantity - b.quantity); // Sort by quantity ascending
  }

  setStorageData(variant: VariantData): void {
    const { id, category, quantity, title, image, price, type, handle, inventory_quantity, product_type, properties, replaceQuantity } = variant;
    const variantKey = `${category}_${id}`;
    const variantInStorage = this.state.variants[variantKey];
    
    if (!variantInStorage && quantity >= 0) {
      // Create new variant entry
      const propertiesList: Record<string, string>[] = [];
      // If properties provided, add to list
      if (properties && Object.keys(properties).length > 0) {
        propertiesList.push(properties);
      }
      
      this.state.variants[variantKey] = {
        id,
        category,
        quantity: quantity,
        title: title || '',
        image: image || '',
        price: price || 0,
        type: type || 'product',
        handle: handle || '',
        inventory_quantity: inventory_quantity || 0,
        product_type: product_type || "",
        propertiesList
      };
    }
    else if (variantInStorage) {
      const oldQty = this.state.variants[variantKey].quantity;
      
      // If replaceQuantity is true, set quantity directly instead of adding
      if (replaceQuantity === true) {
        this.state.variants[variantKey].quantity = quantity;
      } else {
        this.state.variants[variantKey].quantity += quantity;
      }

      const newQty = this.state.variants[variantKey].quantity;

      // Update all fields to ensure variant switches are reflected
      if (title !== undefined) {
        this.state.variants[variantKey].title = title;
      }
      if (image !== undefined) {
        this.state.variants[variantKey].image = image;
      }
      if (handle !== undefined) {
        this.state.variants[variantKey].handle = handle;
      }
      if (product_type !== undefined) {
        this.state.variants[variantKey].product_type = product_type;
      }
      if (price !== undefined) {
        this.state.variants[variantKey].price = price;
      }
      
      // Handle propertiesList based on quantity change
      if (!this.state.variants[variantKey].propertiesList) {
        this.state.variants[variantKey].propertiesList = [];
      }
      
      // If quantity increased with new properties, add to list
      if (properties && Object.keys(properties).length > 0) {
        this.state.variants[variantKey].propertiesList.push(properties);
      }
      
      // Only remove from propertiesList if newQty < propertiesList.length
      // Example: qty=4, props=3, remove 1 → qty=3, props=3 (don't remove, qty >= props)
      // Example: qty=3, props=3, remove 1 → qty=2, props=3 (remove 1, so props=2 to match qty)
      const propsLength = this.state.variants[variantKey].propertiesList.length;
      if (newQty < propsLength) {
        const removeCount = propsLength - newQty;
        for (let i = 0; i < removeCount; i++) {
          this.state.variants[variantKey].propertiesList.pop();
        }
      }
      
      // If quantity is 0, clear the list
      if (newQty === 0) {
        this.state.variants[variantKey].propertiesList = [];
      }
    }
    else {
      console.warn("Quantity update failed");
    }
    this.updateStorageTotals();
    this.persist();
    this.notify("UPDATE_VARIANT_QUANTITY");
  }

  setBoxProductData(boxProduct: BoxProductData): void {
    const { id, quantity, title, image, price, handle } = boxProduct;
    const boxKey = `box_${id}`;

    if (!this.state.boxProducts[boxKey]) {
      this.state.boxProducts[boxKey] = {
        id,
        quantity: 0,
        title,
        image,
        price,
        handle,
        type: "box"
      };
    }

    this.state.boxProducts[boxKey].quantity = quantity;
    this.updateStorageTotals();
    this.persist();
    this.notify("UPDATE_BOX_PRODUCT_QUANTITY");
  }

  subscribe(callback: StateCallback): () => void {
    this.subscribers.add(callback);
    // Return unsubscribe function
    return () => this.subscribers.delete(callback);
  }

  notify(actionName: string): void {
    const state = this.state;
    const stateUpdate: StateUpdate = {
      action: actionName,
      // For the first action, use currentState as previousState
      previousState: this.previousState ?? state,
      state: state,
    };

    this.subscribers.forEach((onStateChange) => onStateChange(stateUpdate));

    // Store current state as previous for next update    
    this.previousState = { ...state };
  }

  updateStorageTotals(): void {
    let totalQuantity = 0;
    let totalPrice = 0;
    let totalPriceWithoutDiscount = 0;

    // Calculate totals for mix-and-match products (these get discounts)
    for (const productKey in this.state.variants) {
      const product = this.state.variants[productKey];
      totalQuantity += product.quantity;
      totalPriceWithoutDiscount += product.price * product.quantity;
    }

    this.state.totalQuantity = totalQuantity;
    this.state.totalPriceWithoutDiscount = totalPriceWithoutDiscount;
    this.state.currentDiscount = this._calculateVolumeDiscount(totalQuantity);

    // Apply discount to mix-and-match products
    for (const productKey in this.state.variants) {
      const product = this.state.variants[productKey];
      totalPrice += Math.ceil(product.price * ((100 - (this.state.currentDiscount || 0)) / 100)) * product.quantity;
    }

    this.state.totalPrice = totalPrice;
  }

  _calculateVolumeDiscount(totalQuantity: number): number {
    const matchingTier = this.state.discountTiers
      .filter((tier) => totalQuantity >= tier.quantity)
      .pop(); // Get last (highest) matching tier

    return matchingTier ? matchingTier.discount : 0;
  }

  _setMainCTAText(status: string): void {
    let buttonStatus = '';
    const bundleCancelEls = document.querySelectorAll("[data-bundle-cancel]");
    const bundleAnyItemEls = document.querySelectorAll("[data-bundle-any_item]");
    const bundleButtonTextEls = [bundleCancelEls, bundleAnyItemEls];
    if (buttonStatus != status) {
      bundleButtonTextEls.map((allButtons) => {
        allButtons.forEach((el) => {
          if (el.hasAttribute("data-bundle-" + status)) {
            el.classList.remove("tw-hidden");
            const parent = el.parentElement as HTMLElement;
            if (parent) {
              parent.removeAttribute("style");
            }
          } else {
            el.classList.add("tw-hidden");
          }
        });
      });
      buttonStatus = status;
    }
  }

  _setBundleEditMode(): void {
    const [baseUrl] = window.location.search.split("edit_bundle");

    // Find the existing bundle in the cart that we're editing
    const cartItem = window.cart.items.find((e: CartItem) => e.key == window.location.search.split("edit_bundle=")[1]?.split("&")[0]);
    if (!cartItem) {
      console.warn("Cart item not found");
      return;
    }
    const addonsListString = cartItem.properties._bundle_addons;
    const bundleItemsString = cartItem.properties['__foxsell:dynamic_add_on_bundle_items'];

    this.clearState();

    this._fillStateVariantsFromCartItem(bundleItemsString);
    this._fillStateGiftsFromAddonsList(addonsListString);

    this._setMainCTAText("cancel");

    const bundleId = window.location.search.split("edit_bundle=")[1]?.split("&")[0] || '';
    document.querySelectorAll("[data-mm-add-to-cart]").forEach(el => {
      el.setAttribute("data-mm-add-to-cart", bundleId);
    });

    // Prevents orphaned state that could cause issues on return
    window.addEventListener("beforeunload", () => {
      this.clearState();
      this.updateStorageTotals();
      this.persist();
    });

    history.replaceState("", "", `${location.pathname}${baseUrl}`);
  }

  _fillStateVariantsFromCartItem(bundleItemsString?: string): void {
    if (!bundleItemsString) {
      console.warn("No bundle items found");
      return;
    }

    const currentVariants = this.state.variants;
    JSON.parse(bundleItemsString).forEach((bundleItem: any) => {
      const id = bundleItem.variantId;
      const key = Object.keys(currentVariants).find(k => k.includes(String(id)));
      if (key) {
        this.state.variants[key].quantity += (bundleItem.quantity || 0);
        
        // Restore handle from bundle item properties if state doesn't have it
        if (bundleItem.properties?._handle && !this.state.variants[key].handle) {
          this.state.variants[key].handle = bundleItem.properties._handle;
        }
        
        // Restore engraving properties from cart item
        if (bundleItem.properties && Object.keys(bundleItem.properties).length > 0) {
          const nonInternalKeys = Object.keys(bundleItem.properties).filter(propKey => !propKey.startsWith('_'));
          const hasEngravingData = nonInternalKeys.length > 0;
          
          if (hasEngravingData) {
            if (!this.state.variants[key].propertiesList) {
              this.state.variants[key].propertiesList = [];
            }
            this.state.variants[key].propertiesList.push(bundleItem.properties);
          }
        }
        
        this.updateStorageTotals();
        this.notify("UPDATE_VARIANT_QUANTITY");
      } else {
        console.warn("No key found in MixStorage for variantId:", id);
      }
    });
  }

  _fillStateGiftsFromAddonsList(addonsListString?: string): void {
    if (!addonsListString) {
      console.warn("No addons list found");
      return;
    }

    addonsListString.split(",").forEach(addon => {
      const [addonHandle, addonQuantityStr] = addon.split(":");
      const addonQuantity = parseInt(addonQuantityStr, 10);
      if (addonHandle && !isNaN(addonQuantity)) {
        this.updateGiftCheckedState({
          gift_qnt: addonQuantity,
          gift_handle: addonHandle,
        });
        this.notify("GIFT_CHECKED_STATE_UPDATED");
      } else {
        console.warn("Invalid addon entry:", addon);
      }
    });
  }
}

class NavBlock extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback(): void {
    this.addEventListener("click", () => {
      document.querySelectorAll("mm-nav-block").forEach(el => el.classList.remove("active"));
      this.classList.add("active");
      const sectionEls = document.querySelectorAll('[data-nav-title]');
      const activeSectionEl = document.querySelector("[data-nav-title='" + this.getAttribute("data-scroll") + "']");

      sectionEls.forEach(el => {
        el.classList.remove('active');
      });
      if (activeSectionEl) {
        activeSectionEl.classList.add('active');
      }
      
      // Scroll to top of mix-and-match section
      const mixAndMatchSection = document.querySelector('mix-and-match');
      if (mixAndMatchSection) {
        const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const targetPosition = mixAndMatchSection.getBoundingClientRect().top + window.pageYOffset - headerHeight;
        
        window.scrollTo({
          top: targetPosition + 4,
          behavior: 'smooth'
        });
      }
    });
  }
}

// Dynamic Height Manager for CSS Variables
class DynamicHeightManager {
  constructor() {
    this.init();
    this.bindEvents();
  }

  init(): void {
    this.updateHeights();
  }

  updateHeights(): void {
    // Get navigation height
    const navElement = document.querySelector('#topNavigation') as HTMLElement | null;
    if (navElement) {
      const navHeight = navElement.offsetHeight;
      document.documentElement.style.setProperty('--nav-height', `${navHeight}px`);
    }

    // Get header height (mix-and-match main element)
    const headerElements = document.querySelectorAll('header.header');
    const headerElement = Array.from(headerElements).find((el) => !!el.getBoundingClientRect().height);
    if (headerElement) {
      const headerHeight = headerElement.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    }

    // Alternative: Get first category header height if you want that instead
    const categoryHeader = document.querySelector('[data-nav-title] .tw-flex.tw-items-center.tw-justify-between') as HTMLElement | null;
    if (categoryHeader) {
      const categoryHeaderHeight = categoryHeader.offsetHeight;
      document.documentElement.style.setProperty('--category-header-height', `${categoryHeaderHeight}px`);
    }
  }

  bindEvents(): void {
    // Update heights on window resize
    window.addEventListener('resize', () => {
      this.updateHeights();
    });

    // Update heights when content loads (in case elements aren't ready initially)
    window.addEventListener('load', () => {
      this.updateHeights();
    });

    // Update when DOM content is loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.updateHeights();
      });
    }
  }
}

// Global cart cache
window.cachedCart = window.cart || { items: [] };

window.fetchAndCacheCart = async function (): Promise<Cart> {
  try {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }
    window.cachedCart = await response.json();
    return window.cachedCart;
  } catch (error) {
    console.warn('Error fetching cart:', error);
    window.cachedCart = { items: [] };
    return window.cachedCart;
  }
};

document.addEventListener("DOMContentLoaded", async () => {
  if (!document.querySelector("mm-product-card")) {
    console.warn("No mm-product-card found");
    return;
  }

  await window.fetchAndCacheCart();

  window.mixMatchStore = new MixAndMatchStore();

  // Initialize dynamic height manager for CSS variables
  window.heightManager = new DynamicHeightManager();

  customElements.define("mm-product-card", MMProductCard);
  customElements.define("mm-cart-summary", MMCartSummary);
  customElements.define("mm-nav-block", NavBlock);
  customElements.define("mm-box-product", MMBoxProduct);

  // Handle edit_bundle mode AFTER custom elements are defined
  // This ensures all components are subscribed and will receive the notifications
  if (window.location.search.includes("edit_bundle")) {
    console.log("ENTERING EDIT MODE");
    (window.mixMatchStore as MixAndMatchStore)._setBundleEditMode();
  }

  // Add sticky class to navigation when it sticks
  const stickyNav = document.querySelector('.js-sticky-nav') as HTMLElement | null;
  if (stickyNav) {
    const topOffset = parseInt(stickyNav.dataset.topOffset || '0', 10);
    
    // Create a sentinel element just before the sticky nav
    const sentinel = document.createElement('div');
    sentinel.style.height = '1px';
    sentinel.style.position = 'absolute';
    sentinel.style.top = '0';
    sentinel.style.pointerEvents = 'none';
    if (stickyNav.parentNode) {
      stickyNav.parentNode.insertBefore(sentinel, stickyNav);
    }

    let sentinelIsVisible = true;
    let navIsSticky = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        sentinelIsVisible = entry.isIntersecting;
      },
      {
        threshold: [0],
        rootMargin: `-${topOffset}px 0px 0px 0px`
      }
    );

    const handleScroll = () => {
      const shouldStick = !sentinelIsVisible;
      if (shouldStick === navIsSticky) return;

      stickyNav.classList.toggle('is-sticky', shouldStick);
      navIsSticky = shouldStick;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Start observing immediately (will update sentinel visibility)
    observer.observe(sentinel);
  }
});
